import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dealService, introducerService } from '../services/api';
import '../styles/CreateDeal.css';
import Sidebar from '../components/Sidebar';

export default function CreateDeal() {
  const navigate = useNavigate();
  const _user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = _user.role === 'platform_owner';
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [introducers, setIntroducers] = useState([]);
  const [leadId, setLeadId] = useState(null);
  const [enquiryId, setEnquiryId] = useState(null);
  const [orgMembers, setOrgMembers] = useState(null);

  const [form, setForm] = useState({
    introducer_id:  searchParams.get('introducer_id')  || '',
    client_name:    searchParams.get('client_name')    || '',
    client_address: searchParams.get('client_address') || '',
    client_phone:   searchParams.get('client_phone')   || '',
    client_email:   searchParams.get('client_email')   || '',
    client_notes:   searchParams.get('client_notes')   || '',
    product_type:   searchParams.get('product_type')   || '',
    lender_proc_fee: '',
    client_fee:      '',
  });

  const lockedIntroducer = !!searchParams.get('lead_id') && !!searchParams.get('introducer_id');

  useEffect(() => {
    const lid = searchParams.get('lead_id');
    const eid = searchParams.get('enquiry_id');
    if (lid) setLeadId(lid);
    if (eid) setEnquiryId(eid);
    if (lockedIntroducer) {
      loadOrgMembers(searchParams.get('introducer_id'));
    } else {
      loadIntroducers();
    }
  }, []); // eslint-disable-line

  const loadOrgMembers = async (userId) => {
    try {
      const res = await fetch(`/api/introducers/org-members/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      setOrgMembers(data.members || []);
    } catch (err) { setOrgMembers([]); }
  };

  const loadIntroducers = async () => {
    try {
      const response = await introducerService.list();
      setIntroducers(response.data.introducers || []);
    } catch (err) {
      setError('Failed to load introducers');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.client_name || !form.client_phone || !form.client_email) {
      setError('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await dealService.create({ ...form, lead_id: leadId, enquiry_id: enquiryId });
      navigate(isOwner ? '/owner' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  const fromLead = !!searchParams.get('lead_id') || !!searchParams.get('enquiry_id');
  const backPath = fromLead ? '/leads' : (isOwner ? '/owner' : '/dashboard');
  const backLabel = fromLead ? 'Back to Leads' : (isOwner ? 'Back to Dashboard' : 'Back to Dashboard');

  const formBody = (
    <>
      {fromLead && (
        <div className="lead-convert-banner">
          ✓ Pre-filled from approved lead — review details and submit to create the deal.
        </div>
      )}
      <div className={isOwner ? '' : 'main'}>
        <form onSubmit={handleSubmit} className="form" style={isOwner ? { background:'#fff', borderRadius:'12px', border:'1px solid #e5e7eb', padding:'28px', boxShadow:'none', maxWidth:'580px' } : {}}>
          <section className="form-section">
            <h2>Introducer{!lockedIntroducer && <span style={{ fontWeight: 400, fontSize: 14, color: '#9ca3af' }}> (optional)</span>}</h2>
            <div className="form-group">
              {lockedIntroducer ? (
                orgMembers && orgMembers.length > 1 ? (
                  <>
                    <label>Assign within {orgMembers[0].company_name || orgMembers[0].org_name || 'the introducer organisation'}</label>
                    <select name="introducer_id" value={form.introducer_id} onChange={handleChange}>
                      {orgMembers.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}{m.introducer_level ? ` (${m.introducer_level})` : ''}</option>
                      ))}
                    </select>
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>
                      This lead came from this organisation — you can credit the deal to a different team member, but not change the organisation.
                    </p>
                  </>
                ) : (
                  <>
                    <label>Introducer</label>
                    <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#166534' }}>
                      🔒 {(orgMembers && orgMembers[0] && orgMembers[0].name) || 'Introducer from lead'}
                    </div>
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: '6px 0 0' }}>Fixed — this deal was introduced by this introducer via an accepted lead.</p>
                  </>
                )
              ) : (
                <>
                  <label>Introducer</label>
                  <select name="introducer_id" value={form.introducer_id} onChange={handleChange}>
                    <option value="">— Direct / No introducer —</option>
                    {introducers.map((intro) => (
                      <option key={intro.id} value={intro.id}>{intro.name}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </section>

          <section className="form-section">
            <h2>Client Information</h2>
            <div className="form-group">
              <label>Client Name *</label>
              <input type="text" name="client_name" value={form.client_name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input type="text" name="client_address" value={form.client_address} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" name="client_phone" value={form.client_phone} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="client_email" value={form.client_email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea name="client_notes" value={form.client_notes} onChange={handleChange} rows="4" />
            </div>
          </section>

          <section className="form-section">
            <h2>Deal Details</h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '-4px 0 12px' }}>
              Confirm these rather than leaving them to guesswork — nothing here is inferred automatically.
            </p>
            <div className="form-group">
              <label>Product Type</label>
              <select name="product_type" value={form.product_type} onChange={handleChange}>
                <option value="">— Select —</option>
                <option value="residential_purchase">Residential Purchase</option>
                <option value="residential_remortgage">Residential Remortgage</option>
                <option value="buy_to_let_purchase">Buy to Let Purchase</option>
                <option value="buy_to_let_remortgage">Buy to Let Remortgage</option>
                <option value="commercial">Commercial</option>
                <option value="bridging">Bridging</option>
                <option value="protection">Protection</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Lender Proc Fee (£)</label>
              <input type="number" step="0.01" min="0" name="lender_proc_fee" value={form.lender_proc_fee} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Client Fee (£)</label>
              <input type="number" step="0.01" min="0" name="client_fee" value={form.client_fee} onChange={handleChange} />
            </div>
          </section>

          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Creating...' : fromLead ? 'Convert to Deal' : 'Create Deal'}
            </button>
            <button type="button" onClick={() => navigate(backPath)} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );

  if (isOwner) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <main className="dashboard-main">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
            <h1 style={{ fontSize:'24px', fontWeight:700, margin:0 }}>{fromLead ? 'Convert Lead to Deal' : 'Create Deal'}</h1>
            <button onClick={() => navigate(backPath)} style={{ background:'#6b7280', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 16px', cursor:'pointer', fontWeight:600, fontSize:'13px' }}>
              {backLabel}
            </button>
          </div>
          {formBody}
        </main>
      </div>
    );
  }

  return (
    <div className="create-deal">
      <header className="header">
        <h1>{fromLead ? 'Convert Lead to Deal' : 'Create Deal'}</h1>
        <button onClick={() => navigate(backPath)} className="back-btn">
          {backLabel}
        </button>
      </header>
      {formBody}
    </div>
  );
}
